import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowLeft, ArrowRight } from "lucide-react"
import { toast } from "sonner"
import { CHANNEL_STATUS_LABELS, PROVIDER_LABELS, type ChannelConnectionDetail } from "@vienna/contracts"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { ChannelIcon } from "@/components/workspace/channel-icon"
import { DataTable, Heading, Hint } from "@/components/workspace/primitives"
import { api, ApiError } from "@/lib/api"
import { formatDateTime } from "@/lib/format"
import { connectionQuery } from "@/lib/queries"
import { useCan, useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/channels/$connectionId")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(connectionQuery(params.workspace, params.connectionId)),
  component: ChannelHealth,
})

function longDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "long", year: "numeric" })
}

/** Screen 07. Which capabilities work, what depends on them, and how to repair access. */
function ChannelHealth() {
  const { workspace } = useWorkspaceContext()
  const can = useCan()
  const { connectionId } = Route.useParams()
  const queryClient = useQueryClient()
  const query = connectionQuery(workspace.slug, connectionId)
  const connection = useSuspenseQuery(query).data
  const params = { workspace: workspace.slug }
  const healthy = connection.status === "connected"
  const caps = connection.capabilities
  const commentsMissing = caps.read_comments !== "available"
  const dmMissing = caps.receive_messages !== "available"

  const check = useMutation({
    mutationFn: () => api<{ queued: true }>(`/w/${workspace.slug}/channels/${connectionId}/check`, { method: "POST" }),
    onSuccess: () => {
      toast("Checking the connection", { description: "Results appear here in a moment." })
      for (const delay of [1500, 4000, 8000]) setTimeout(() => void queryClient.invalidateQueries({ queryKey: query.queryKey }), delay)
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not check the connection"),
  })

  const rows: Array<[string, ChannelConnectionDetail["status"] | "available" | "missing" | "paused", React.ReactNode]> = [
    [
      "Direct messages",
      dmMissing ? "missing" : "available",
      dmMissing ? (
        "Reconnect and grant messaging access"
      ) : (
        <Button variant="link" asChild>
          <Link to="/w/$workspace/inbox" params={params}>
            Open inbox
            <ArrowRight />
          </Link>
        </Button>
      ),
    ],
    ["Read and reply to comments", commentsMissing ? "missing" : "available", commentsMissing ? "Reconnect and grant comment access" : "Working"],
    [
      "Comment moderation",
      commentsMissing ? "paused" : "available",
      <Button variant="link" asChild>
        <Link to="/w/$workspace/settings/moderation" params={params}>
          Review rules
          <ArrowRight />
        </Link>
      </Button>,
    ],
    [
      "Comment-to-DM triggers",
      caps.private_replies !== "available" ? "paused" : "available",
      <Button variant="link" asChild>
        <Link to="/w/$workspace/automations" params={params}>
          {connection.affected.pausedAutomations} affected {connection.affected.pausedAutomations === 1 ? "automation" : "automations"}
          <ArrowRight />
        </Link>
      </Button>,
    ],
  ]

  return (
    <div className="page-wrap ws-workspace">
      <Heading
        title={`${PROVIDER_LABELS[connection.provider]} connection`}
        description="Account access and the workflows that depend on it."
        action={
          <Button variant="outline" asChild>
            <Link to="/w/$workspace/settings/channels" params={params}>
              <ArrowLeft />
              All channels
            </Link>
          </Button>
        }
      />
      <div className="ws-connection-head">
        <span className="ws-feature-icon">
          <ChannelIcon provider={connection.provider} />
        </span>
        <div>
          <h2>{connection.handle ?? connection.displayName}</h2>
          <p>
            {connection.displayName} · connected by {connection.connectedByName}
          </p>
        </div>
        <Badge variant={healthy ? "secondary" : "outline"} className={connection.status === "needs_attention" ? "ws-status-warning" : ""}>
          {CHANNEL_STATUS_LABELS[connection.status]}
        </Badge>
        {can("manage_workspace") && (
          <Button asChild>
            <Link to="/w/$workspace/channels/new" params={params} search={{ provider: connection.provider }}>
              {healthy ? "Reconnect" : "Review access & reconnect"}
              <ArrowRight />
            </Link>
          </Button>
        )}
      </div>
      {!healthy && connection.issueSummary && (
        <Hint title={connection.status === "disconnected" ? "This account is disconnected" : "Access changed"} warning>
          {connection.issueSummary} {commentsMissing && !dmMissing ? "Messages remain available. Comment triggers and moderation are paused until access is restored." : ""}
        </Hint>
      )}
      <div className="ws-health-grid">
        <Card>
          <CardHeader>
            <CardTitle>What is available</CardTitle>
            <CardDescription>Access issues affect only the capabilities listed below.</CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              heads={["Capability", "Status", "Next step"]}
              rowKeys={rows.map(([name]) => name)}
              rows={rows.map(([name, state, next]) => [
                name,
                <Badge variant={state === "available" ? "secondary" : "outline"}>{state === "available" ? "Available" : state === "missing" ? "Access missing" : "Paused"}</Badge>,
                next,
              ])}
            />
            <p className="ws-hint mt-5">After reconnecting, review paused work and any missed events before resuming.</p>
          </CardContent>
        </Card>
        <div className="ws-detail-list">
          <h2>Connection details</h2>
          {[
            ["Last checked", connection.lastCheckedAt ? formatDateTime(connection.lastCheckedAt) : "Not yet"],
            ["Last successful sync", connection.lastSyncedAt ? formatDateTime(connection.lastSyncedAt) : "No events yet"],
            ["Issue detected", connection.issueDetectedAt ? `${connection.status === "disconnected" ? "Access lost" : "Permission changed"} · ${formatDateTime(connection.issueDetectedAt)}` : "None"],
            ["Connected on", longDate(connection.connectedAt)],
          ].map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
          {can("manage_workspace") && (
            <Button variant="outline" onClick={() => check.mutate()} disabled={check.isPending || connection.status === "disconnected"}>
              Check again
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
